import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ProjectCard } from './ProjectCard';
import { Layers } from 'lucide-react';

export function SortableProjectCard({
  project,
  isOver,
  combineMode = false,
  dwellProgress = 0,
  ...props
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.id.toString() });

  // Only disable transforms when THIS card is the combine target (user is dwelling)
  const isCombineTarget = isOver && combineMode;

  const style = {
    // Apply transform unless this is a combine target (then we freeze position)
    transform: isCombineTarget ? undefined : CSS.Transform.toString(transform),
    transition: isCombineTarget ? 'transform 0.2s ease-out' : transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto'
  };

  // Visual feedback classes based on dwell state
  const getDwellClasses = () => {
    if (!isOver) return '';

    // Dwell complete (>= 100%) - strong visual feedback, ready to combine
    if (combineMode && dwellProgress >= 100) {
      return 'ring-4 ring-blue-500 scale-[1.02] shadow-xl rounded-lg transition-all duration-200';
    }

    // In combine zone with dwell in progress - subtle feedback with progress bar
    if (combineMode && dwellProgress > 0) {
      return 'ring-2 ring-blue-400 rounded-lg transition-all duration-150';
    }

    // In combine zone but dwell hasn't started yet
    if (combineMode) {
      return 'ring-2 ring-blue-300 rounded-lg transition-all duration-150';
    }

    return '';
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-id={project.id.toString()}
      {...attributes}
      {...listeners}
      className={`relative ${getDwellClasses()}`}
    >
      {/* Dwell progress indicator */}
      {isOver && dwellProgress > 0 && !combineMode && (
        <div className="absolute inset-0 pointer-events-none z-10 rounded-lg overflow-hidden">
          {/* Progress bar at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
            <div
              className="h-full bg-blue-500 transition-all duration-75"
              style={{ width: `${dwellProgress}%` }}
            />
          </div>
          {/* Subtle overlay */}
          <div
            className="absolute inset-0 bg-blue-500 rounded-lg transition-opacity duration-150"
            style={{ opacity: dwellProgress / 500 }}
          />
        </div>
      )}

      {/* Combine mode indicator - only show when dwell is complete */}
      {isCombineTarget && dwellProgress >= 100 && (
        <div className="absolute inset-0 pointer-events-none z-10 flex items-center justify-center">
          <div className="absolute inset-0 bg-blue-500/10 rounded-lg" />
          <div className="bg-blue-500 text-white rounded-full p-2 shadow-lg animate-pulse">
            <Layers className="w-5 h-5" />
          </div>
        </div>
      )}

      <ProjectCard project={project} {...props} />
    </div>
  );
}
